import { useState } from "react";
import { featuredReviews } from "@/mocks/home";
import SectionHeading from "./SectionHeading";

const avatarColors = [
  "bg-primary-200 text-primary-800",
  "bg-accent-200 text-accent-800",
  "bg-secondary-200 text-secondary-800",
  "bg-background-300 text-foreground-700",
];

const hotBadge: Record<string, string> = {
  "火": "bg-primary-500 text-background-50",
  "赞": "bg-accent-500 text-background-50",
  "议": "bg-secondary-500 text-background-50",
  "聊": "bg-secondary-100 text-secondary-800",
};

function ReviewCard({ item, index }: { item: (typeof featuredReviews)[number]; index: number }) {
  const [up, setUp] = useState(item.agree);
  const [down, setDown] = useState(item.against);
  const [myVote, setMyVote] = useState<0 | 1 | 2>(0);

  const vote = (kind: 1 | 2) => {
    if (myVote === kind) {
      if (kind === 1) setUp(item.agree);
      else setDown(item.against);
      setMyVote(0);
      return;
    }
    if (myVote === 1) setUp(item.agree);
    if (myVote === 2) setDown(item.against);
    if (kind === 1) setUp((n) => String(Number(n) + 1));
    else setDown((n) => String(Number(n) + 1));
    setMyVote(kind);
  };

  return (
    <article className="flex flex-col rounded-2xl border border-background-200 bg-background-50 p-6 transition-colors hover:border-background-300">
      <header className="flex items-center gap-3">
        <span
          className={`flex h-10 w-10 items-center justify-center rounded-full text-base font-semibold ${
            avatarColors[index % avatarColors.length]
          }`}
          aria-hidden="true"
        >
          {item.avatar}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-semibold text-foreground-950">{item.user}</h3>
            <span className="rounded-full bg-background-100 px-2 py-0.5 text-xs text-foreground-600">{item.role}</span>
          </div>
          <p className="mt-0.5 text-xs text-foreground-600">正在讨论：{item.topic}</p>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${hotBadge[item.hot]}`}>{item.hot}</span>
      </header>

      <blockquote className="mt-4 flex-1 rounded-xl bg-background-100 p-4 text-sm leading-relaxed text-foreground-800">
        <i className="ri-double-quotes-l mr-1 text-primary-300" aria-hidden="true"></i>
        {item.comment}
      </blockquote>

      <footer className="mt-4 flex flex-wrap items-center gap-2 border-t border-background-200 pt-4">
        <button
          type="button"
          onClick={() => vote(1)}
          className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
            myVote === 1
              ? "bg-accent-500 text-background-50"
              : "bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-800"
          }`}
          aria-pressed={myVote === 1}
        >
          <i className={myVote === 1 ? "ri-thumb-up-fill" : "ri-thumb-up-line"} aria-hidden="true"></i>
          认同 {up}
        </button>
        <button
          type="button"
          onClick={() => vote(2)}
          className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
            myVote === 2
              ? "bg-primary-500 text-background-50"
              : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-800"
          }`}
          aria-pressed={myVote === 2}
        >
          <i className={myVote === 2 ? "ri-thumb-down-fill" : "ri-thumb-down-line"} aria-hidden="true"></i>
          存疑 {down}
        </button>
        <span className="ml-auto hidden items-center gap-1 whitespace-nowrap text-xs text-foreground-500 sm:inline-flex">
          <i className="ri-chat-3-line" aria-hidden="true"></i>
          查看完整讨论
        </span>
      </footer>
    </article>
  );
}

export default function Reviews() {
  return (
    <section id="reviews" className="bg-background-100 py-12 md:py-16">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <SectionHeading
            kicker="观点广场 · 精选热评"
            title="网友怎么看，先来点有含金量的"
            desc="每条都是被瓜友认同或争论过的代表性声音。点赞认同、点踩存疑，你的每次表态都在帮助观点排序。"
          />
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
          {featuredReviews.map((r, i) => (
            <ReviewCard key={r.topic} item={r} index={i} />
          ))}
        </div>

        <p className="mt-6 text-center text-xs text-foreground-600">
          以上热评仅代表发言网友个人观点，仅供多角度参考，不构成对任何事件或人物的定论。
        </p>
      </div>
    </section>
  );
}